import { useEffect, useState, useRef } from "react";
import { 
  GetDataFromApiWithParams, 
  ExecutePostWithParams 
} from "../../../../services/api/ExecuteApiRequests";
import IPDCFinalTable from "../../../../components/IPDCFinalTable";
import { Box, Button, Alert, CircularProgress, Typography, Card, CardContent, Chip } from "@mui/material";
import { Check, Close, Download, TableChart, Visibility } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function RenewalApprovalPage() {
  const navigate = useNavigate();
  const [renewals, setRenewals] = useState([]);
  const componentRef = useRef();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchVerifiedRenewals();
  }, []);

  const fetchVerifiedRenewals = async () => {
    try {
      setLoading(true);
      setError("");
      console.log("Fetching verified renewal requests from API...");
      
      // You'll need to create this endpoint in your backend
      const resultRow = await GetDataFromApiWithParams("agreements/renewals/pending/approval");
      console.log("Verified Renewals API Response:", resultRow);
      
      if (resultRow && Array.isArray(resultRow)) {
        console.log(`Total verified renewals received: ${resultRow.length}`);
        
        if (resultRow.length > 0) {
          console.log("First verified renewal sample:", resultRow[0]);
          setRenewals(resultRow);
          
          // Define columns to show for renewal approval
          const importantColumns = [
            'id', 'original_agreement_id', 'company_name', 'tenant_id',
            'ownership_type', 'investment_type', 'verification_date',
            'verified_by', 'contract_issue', 'contract_renewal', 
            'monthly_rent', 'area_for_calculation', 'status'
          ];
          
          // Use important columns that actually exist in the data
          const availableColumns = importantColumns.filter(col => 
            resultRow[0].hasOwnProperty(col)
          );
          
          console.log("Columns available for display:", availableColumns);
          setSelectedColumns([...availableColumns, 'actions']);
        } else {
          setRenewals([]);
          setError("No verified renewal requests found for approval.");
        }
      } else {
        setError("No renewal data received from API.");
      }
    } catch (err) {
      console.error("Error fetching verified renewals:", err);
      setError("Error fetching verified renewals: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (renewal, action) => {
    try {
      const renewalId = renewal.id || renewal.renewal_id;
      const agreementId = renewal.original_agreement_id || renewal.agreement_id;
      
      if (!renewalId) {
        setError("Cannot process: Renewal ID is missing");
        return;
      }

      const updateData = {
        renewal_id: renewalId,
        agreement_id: agreementId,
        action: action,
        remarks: `Renewal request ${action}ed by Deputy CEO`,
        approved_by: "deputy_ceo",
        approval_date: new Date().toISOString().split('T')[0]
      };

      console.log(`Sending ${action} for renewal:`, renewalId);
      // You'll need to create this endpoint in your backend
      const response = await ExecutePostWithParams("agreements/renewals/approve", updateData);
      
      if (response) {
        // Remove the processed renewal from the list
        setRenewals(prev => prev.filter(item => 
          (item.id || item.renewal_id) !== renewalId
        ));
        setSuccess(`Renewal request successfully ${action === "approve" ? "approved" : "rejected"}!`);
        setError("");
        setTimeout(() => setSuccess(""), 5000);
        
        console.log(`Renewal ${renewalId} ${action}ed successfully`);
      } else {
        setError(`Failed to ${action} renewal request. Server returned no response.`);
        setSuccess("");
      }
    } catch (err) {
      console.error("Approval error:", err);
      setError(`Error: ${err.message || "Unknown error during approval"}`);
      setSuccess("");
    }
  };

  const handleViewDetails = (renewal) => {
    console.log("View details for renewal:", renewal);
    // You can implement a modal or navigate to details page
    // navigate(`/renewals/${renewal.id}/details`);
  };

  const handleExportToExcel = (renewal) => {
    try {
      const headers = Object.keys(renewal).filter(key => 
        key !== 'actions' && 
        typeof renewal[key] !== 'object' &&
        !key.startsWith('_')
      );
      
      const csvHeaders = headers.join(',');
      const csvRow = headers.map(header => {
        const value = renewal[header];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(',');
      
      const csvContent = `${csvHeaders}\n${csvRow}`;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `renewal_${renewal.id || renewal.renewal_id}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported renewal ${renewal.id || renewal.renewal_id} to Excel`);
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      setError("Error exporting renewal data to Excel");
    }
  };

  const handleExportAllToExcel = () => {
    try {
      if (enhancedRenewals.length === 0) {
        setError("No data to export");
        return;
      }

      const headers = Object.keys(enhancedRenewals[0]).filter(key => 
        key !== 'actions' && 
        typeof enhancedRenewals[0][key] !== 'object' &&
        !key.startsWith('_')
      );
      
      const csvHeaders = headers.join(',');
      
      const csvRows = enhancedRenewals.map(renewal => {
        return headers.map(header => {
          const value = renewal[header];
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',');
      }).join('\n');
      
      const csvContent = `${csvHeaders}\n${csvRows}`;
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `all_verified_renewals_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported all ${enhancedRenewals.length} renewals to Excel`);
      setSuccess(`All ${enhancedRenewals.length} renewal requests exported successfully!`);
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      console.error("Error exporting all to Excel:", err);
      setError("Error exporting all renewal data to Excel");
    }
  };

  // Enhanced renewals with action buttons
  const enhancedRenewals = renewals.map(renewal => {
    const renewalId = renewal.id || renewal.renewal_id;
    const status = renewal.status || 'Verified';
    
    // Determine status color
    const getStatusColor = (status) => {
      switch(status?.toLowerCase()) {
        case 'verified': return 'info';
        case 'approved': return 'success';
        case 'rejected': return 'error';
        default: return 'default';
      }
    };
    
    return {
      ...renewal,
      id: renewalId,
      status: (
        <Chip 
          label={status} 
          color={getStatusColor(status)}
          size="small"
        />
      ),
      actions: (
        <Box sx={{ 
          display: "flex", 
          gap: 1, 
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          {/* <Button
            variant="outlined"
            color="info"
            size="small"
            startIcon={<Visibility />}
            onClick={() => handleViewDetails(renewal)}
            disabled={!renewalId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            View
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<Download />}
            onClick={() => handleExportToExcel(renewal)}
            disabled={!renewalId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Export
          </Button> */}
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<Check />}
            onClick={() => handleApproval(renewal, "approve")}
            disabled={!renewalId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<Close />}
            onClick={() => handleApproval(renewal, "reject")}
            disabled={!renewalId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Reject
          </Button>
        </Box>
      )
    };
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading verified renewal requests...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold', mb: 3 }}>
            Renewal Requests - Approval
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          {enhancedRenewals.length > 0 ? (
            <>
              <IPDCFinalTable
                ref={componentRef}
                data={enhancedRenewals}
                setData={setRenewals}
                selectedColumns={selectedColumns}
                title={`Verified Renewal Requests for Approval (${enhancedRenewals.length})`}
                hasAttachment={false}
              />
              
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<TableChart />}
                  onClick={handleExportAllToExcel}
                  disabled={enhancedRenewals.length === 0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Export All Data ({enhancedRenewals.length} rows)
                </Button>
              </Box>
            </>
          ) : (
            !error && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No verified renewal requests found for approval.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  All verified renewal requests have been processed or there are no requests waiting for approval.
                </Typography>
              </Box>
            )
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
